import { XYLocation } from "@nens/deck-gl";
import * as api from "@nens/threedi/openapi-client";

import { getV3Api } from "../apiConfiguration";
import { AppState, Thunk } from "../App";
import { AmountUnit, convertToM3PerSecond } from "../utils/conversions";
import { durationInputToSeconds } from "../utils/timeConverter";

import { BACK_TO_START_SCREEN, BackToStartScreen } from "./appState";
import { sendMessage } from "./mainGui";
import { RequestStatus } from "./requestStatus";
import * as simulationRedux from "./simulation";

interface PumpMenu {
  duration: string;
  amountOfWater: string;
  amountUnit: AmountUnit;
  status: RequestStatus;
  failedMessage: string;
  // coordinate: Coordinate || null;
  placeOnMapMode: boolean;
}

const INITIAL_STATE: PumpMenu = {
  duration: "1:00",
  amountOfWater: "3.1",
  amountUnit: "m³/s",
  status: "NOT_SENT",
  failedMessage: "",
  placeOnMapMode: false,
};

export const getAmountOfWater = (state: AppState) => state.pumpMenu.amountOfWater;
export const getAmountUnit = (state: AppState) => state.dischargeMenu.amountUnit;
export const getDuration = (state: AppState) => state.pumpMenu.duration;
export const getStatus = (state: AppState) => state.pumpMenu.status;
export const getFailedMessage = (state: AppState) => state.pumpMenu.failedMessage;
export const getPlaceOnMapMode = (state: AppState) => state.pumpMenu.placeOnMapMode;

const SET_AMOUNT_OF_WATER = "pumpMenu/SET_AMOUNT_OF_WATER";
const SET_AMOUNT_UNIT = "dischargeMenu/SET_AMOUNT_UNIT";
const SET_DURATION = "pumpMenu/SET_DURATION";
const SET_STATUS = "pumpMenu/SET_STATUS";
const SET_PLACE_ON_MAP_MODE = "pumpMenu/SET_PLACE_ON_MAP_MODE";

interface SetAmountOfWater {
  type: typeof SET_AMOUNT_OF_WATER;
  amountOfWater: string;
}
interface SetAmountUnit {
  type: typeof SET_AMOUNT_UNIT;
  amountUnit: AmountUnit;
}
interface SetDuration {
  type: typeof SET_DURATION;
  duration: string;
}
interface SetStatus {
  type: typeof SET_STATUS;
  failedMessage: string;
  status: RequestStatus;
}
interface SetPlaceOnMapMode {
  type: typeof SET_PLACE_ON_MAP_MODE;
  bool: boolean;
}

type PumpMenuAction =
  | SetAmountOfWater
  | SetAmountUnit
  | SetDuration
  | SetStatus
  | SetPlaceOnMapMode
  | BackToStartScreen;

export const setAmountOfWater = (amountOfWater: string) => {
  return {
    type: SET_AMOUNT_OF_WATER,
    amountOfWater: amountOfWater,
  };
};
export const setAmountUnit = (amountUnit: AmountUnit) => {
  return {
    type: SET_AMOUNT_UNIT,
    amountUnit: amountUnit,
  };
};
export const setDuration = (duration: string) => {
  return {
    type: SET_DURATION,
    duration: duration,
  };
};
export const setStatus = (message: string, status: RequestStatus) => {
  return {
    type: SET_STATUS,
    failedMessage: message,
    status: status,
  };
};
export const setPlaceOnMapMode = (bool: boolean) => {
  return {
    type: SET_PLACE_ON_MAP_MODE,
    bool,
  };
};

export const addPumpToSimulation =
  (coordinate: XYLocation): Thunk =>
  (dispatch, getState) => {
    const state = getState();

    dispatch(setPlaceOnMapMode(false));
    const simulation: api.Simulation | null = simulationRedux.getSimulation(state);
    if (!simulation || simulation.id === undefined) {
      // if no simulation is existing return
      // probably also dispatch error message to user
      dispatch(
        setStatus(
          "Rain event cannot be created, because simulation not found. Check console for details.",
          "NOT_SENT"
        )
      );
      console.error(
        "Discharge event cannot be send, because simulation not found. simulation: ",
        simulation
      );
      return;
    }

    // Pump is added at the current simulation time
    const simulationStatus = simulationRedux.getSimulationStatus(state);
    const offset = simulationStatus.time || 0;
    const value = parseFloat(getAmountOfWater(state));
    const unit = getAmountUnit(state);
    const valueInM3PerSecond = convertToM3PerSecond(value, unit);

    // make request details
    const v3Api = getV3Api();
    const request = {
      simulationPk: simulation.id,
      data: {
        offset,
        duration: durationInputToSeconds(getDuration(state)), // to change it intoo hours
        value: -valueInM3PerSecond,
        units: api.LateralUnitsEnum.M3_S,
        epsg: 4326,
        point: {
          type: "Point",
          coordinates: coordinate,
        },
      },
    };
    // dispatch that request will be sent
    dispatch(setStatus("", "SENT"));
    v3Api
      .simulationsEventsLateralConstantCreate(request)
      .then((_response) => {
        dispatch(setStatus("", "SUCCESS"));
      })
      .catch((error) => {
        dispatch(sendMessage("Placing pump failed: invalid location", 5000, "error"));
        console.log("pump to api failed ____", error);
      });
  };

export default function reducer(state = INITIAL_STATE, action: PumpMenuAction) {
  switch (action.type) {
    case BACK_TO_START_SCREEN:
      return INITIAL_STATE;
    case SET_AMOUNT_OF_WATER: {
      const { amountOfWater } = action;
      return {
        ...state,
        amountOfWater,
      };
    }
    case SET_AMOUNT_UNIT: {
      const { amountUnit } = action;
      return {
        ...state,
        amountUnit,
      };
    }
    case SET_DURATION: {
      const { duration } = action;
      return {
        ...state,
        duration,
      };
    }
    case SET_STATUS: {
      const { failedMessage, status } = action;
      return {
        ...state,
        failedMessage,
        status,
      };
    }
    case SET_PLACE_ON_MAP_MODE: {
      const { bool } = action;
      return {
        ...state,
        placeOnMapMode: bool,
      };
    }
    default:
      return state;
  }
}
